import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { build, pool } from '../../../../../common/classes/request-builder';
import { environment } from '../../../../../../environments/environment';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TratamientosApiService } from '../../../../../service/api/tratamientos-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { VisitasVariedadMesApiService } from '../../../../../service/api/visitas_variedad_mes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import moment from 'moment';

import type {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaSiembraModel } from '../../../../../models/tareas/tarea-siembra.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import { MaquinariaModel } from '../../../../../models/trabajadores/maquinaria.model';
import { SiembraFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-nueva-carga',
    standalone: false,
    templateUrl: './siembra-form.component.html',
    styleUrls: ['./siembra-form.component.scss']
})
export class SiembraFormComponent extends BaseForm<TareaSiembraModel> implements OnInit {
    public readonly appName = environment.appName;
    public readonly applicationType = environment.features.applicationType;
    public readonly horticola = environment.features.horticola;
    public readonly showTareasSiembraProductos = environment.features.showTareasSiembraProductos;
    public readonly showTareasSiembraMaquinarias = environment.features.showTareasSiembraMaquinarias;

    public override model: TareaSiembraModel = {};
    public form!: SiembraFormDefinition;
    public formReady = false;
    public loadingData = true;

    constructor(
        public override route: ActivatedRoute,
        public override router: Router,
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public clientesApi: ClientesApiService,
        public fincasApi: FincasApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
        public productosApi: ProductosApiService,
        public tratamientosApi: TratamientosApiService,
        public visitasVariedadMesApi: VisitasVariedadMesApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.siembra.PUT,
            tareasApi.siembra.POST,
            'tareas-siembra',
            environment.features.horticola ? 'Actualizar Cultivo' : 'Actualizar Tarea Siembra',
            environment.features.horticola ? 'Crear Cultivo' : 'Realizar Tarea Siembra',
            environment.features.horticola ? 'Duplicar Cultivo' : 'Duplicar Tarea Siembra',
        );
    }

    public async ngOnInit(): Promise<void> {
        this.softInit(this.getType());
        this.applyInitialClient();

        this.form = new SiembraFormDefinition(this.getType(), this.getModel());
        this.form.model = this.model;

        await this.initFormData();

        this.formReady = true;
    }

    public formChanges(
        formModel: ReactiveFormValue<TareaSiembraModel>,
        tag: ReactiveFormChangedField<TareaSiembraModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        } as TareaSiembraModel;
        this.model = this.form.model as TareaSiembraModel;

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_cliente') {
            this.clearDependentFields(['id_finca', 'id_sector', 'ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_finca') {
            this.clearDependentFields(['id_sector', 'ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_sector') {
            this.clearDependentFields(['ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'ids_parcelas') {
            this.clearDependentFields(['id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_cultivo') {
            this.clearDependentFields(['id_variedad']);
            this.form.updateCultivoVariedadVisibility();
        }
    }

    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        return pool([
            build(this.clientesApi.clientesGET, this.form.clientes, 'nombre'),
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(
                this.fincasApi.parcelasGET,
                this.form.parcelas,
                this.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
            ),
            build(this.trabajadoresApi.trabajadoresGET, this.form.trabajadores,
                (it: MantenimientoModel) => `${it.nombre} ${it.apellidos || ''}`,
                true,
                true),
            build(this.trabajadoresApi.maquinariaGET, this.form.maquinarias, 'nombre', true),
            build(this.siexApi.cultivosGET, this.form.cultivosPrecedentes, 'cultivo', true),
            build(this.siexApi.cultivosGET, this.form.cultivos, 'cultivo', true),
            build(this.siexApi.variedadesGET, this.form.variedades, 'variedad', true),
            build(this.productosApi.productos.GET, this.form.productos, 'nombre', true),
            build(this.tratamientosApi.tratamientosList, this.form.tratamientos, 'nombre'),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.applySingleFincaDefault();
            this.markProductExpirations();
            this.markMachineryExpirations();
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();
            console.error('Error loading siembra form data.', error);

            return true;
        });
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            horas_totales: horasTotales,
        };
        this.model = this.form.model as TareaSiembraModel;
        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private clearDependentFields(fieldNames: string[]): void {
        const nextModel = { ...this.form.model } as Record<string, unknown>;

        for (const fieldName of fieldNames) {
            nextModel[fieldName] = fieldName === 'id_sector' || fieldName === 'ids_parcelas' ? [] : '';
        }

        this.form.model = nextModel as TareaSiembraModel;
        this.model = this.form.model as TareaSiembraModel;
        this.formBuilderRef?.updateForm(nextModel, false);
    }

    private applyInitialClient(): void {
        if (this.getType() === 0) {
            this.model.id_cliente = StorageManager.getClient()?.id ?? '';
        }
    }

    private applySingleFincaDefault(): void {
        if (this.getType() !== 0 || this.form.fincas.values.length !== 1) {
            return;
        }

        const finca = this.form.fincas.values[0]?.value;

        if (!finca) {
            return;
        }

        this.form.model.id_finca = finca;
        this.model.id_finca = finca;
        this.formBuilderRef?.updateForm({ id_finca: finca }, false);
    }

    private markProductExpirations(): void {
        if (!this.showTareasSiembraProductos) {
            return;
        }

        for (const product of this.form.productos.filtered) {
            if (!product.value?.fecha_caducidad || product.label.startsWith('*')) {
                continue;
            }

            const expirationDate = moment(product.value.fecha_caducidad, 'DD/MM/YYYY');

            if (expirationDate.isBefore(moment())) {
                product.label = '** ' + product.label;
            } else if (expirationDate.isBefore(moment().add(3, 'months'))) {
                product.label = '* ' + product.label;
            }
        }
    }

    private markMachineryExpirations(): void {
        if (!this.showTareasSiembraMaquinarias) {
            return;
        }

        for (const machinery of this.form.maquinarias.filtered) {
            const value = machinery.value as MaquinariaModel | null;

            if (!value?.fecha_ultima_inspeccion || machinery.label.startsWith('*')) {
                continue;
            }

            const dateParts = value.fecha_ultima_inspeccion.toString().split('/');
            const year = parseInt(dateParts[2] ?? '', 10);
            const increment = parseInt(dateParts[0] ?? '', 10) >= 2020 ? 3 : 5;

            dateParts[2] = (year + increment).toString();
            const expirationDate = moment(dateParts.join('/'), 'DD/MM/YYYY');

            if (expirationDate.isBefore(moment())) {
                machinery.label = '** ' + machinery.label;
            } else if (expirationDate.isBefore(moment().add(3, 'months'))) {
                machinery.label = '* ' + machinery.label;
            }
        }
    }

    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev
            ? `${variedad} - ${zonaRiegoAbrev}`
            : variedad;
        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficie = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficieFormatted = superficie.toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const unidades = environment.features.textoSuperficie || 'ha';
        const rightText = [codigos || undefined, `${superficieFormatted} ${unidades}`]
            .filter(Boolean)
            .join(' · ');

        return `${nombre} - ${variedadConZona} — ${rightText}`;
    };
}
